import { useState, useEffect } from 'react'
import { Card, Spin, Empty, Tag, Progress, Radio } from 'antd'
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend, ReferenceLine } from 'recharts'
import { getStockValuationHistory, getStockAnalyst, getStockDividends } from '@/api'
import type { ValuationDetail, AnalystConsensus, DividendItem } from '@/api/stock'

function fmt(val: number | null | undefined, decimals = 2, suffix = ''): string {
    if (val == null || !isFinite(val as number)) return '--'
    return (val as number).toFixed(decimals) + suffix
}

const RATING_COLOR: Record<string, string> = {
    Buy: 'green', Strong_Buy: 'green', Outperform: 'cyan',
    Hold: 'orange', Neutral: 'orange',
    Sell: 'red', Underperform: 'red',
}

type ValMetric = 'pe' | 'pb' | 'ps'

export default function InstitutionalTab({ stockCode }: { stockCode: string }) {
    const [valuation, setValuation] = useState<ValuationDetail | null>(null)
    const [analyst, setAnalyst] = useState<AnalystConsensus | null>(null)
    const [dividends, setDividends] = useState<DividendItem[]>([])
    const [loading, setLoading] = useState(true)
    const [valMetric, setValMetric] = useState<ValMetric>('pe')

    useEffect(() => {
        if (!stockCode) return
        setValuation(null)
        setAnalyst(null)
        setDividends([])
        setLoading(true)
        Promise.allSettled([
            getStockValuationHistory(stockCode, 1).then(d => setValuation(d)),
            getStockAnalyst(stockCode).then(setAnalyst),
            getStockDividends(stockCode, 10).then(setDividends),
        ]).finally(() => setLoading(false))
    }, [stockCode])

    if (loading) {
        return (
            <div className="new-kb-tab institutional-tab">
                <div className="chart-placeholder"><Spin /></div>
            </div>
        )
    }

    const valChartData = [...(valuation?.trend?.historical_items ?? [])].reverse().map(v => ({
        date: v.time_str?.slice(5) ?? '',
        pe: v.pe_ttm ?? null,
        pb: v.pb_mrq ?? null,
        ps: v.ps_ttm ?? null,
    }))

    const metricKey: ValMetric = valMetric
    const metricVals = valChartData.map(v => v[metricKey]).filter((v): v is number => v != null)
    const avgVal = metricVals.length > 0 ? metricVals.reduce((a, b) => a + b, 0) / metricVals.length : null

    const metricLabel: Record<ValMetric, string> = { pe: 'PE TTM', pb: 'PB MRQ', ps: 'PS TTM' }
    const metricColor: Record<ValMetric, string> = { pe: '#1677ff', pb: '#52c41a', ps: '#7c3aed' }

    const ratingColor = analyst?.rating ? (RATING_COLOR[analyst.rating] ?? 'default') : 'default'

    const divChartData = [...dividends].reverse().map(d => ({
        period: d.ex_dividend_date?.slice(0, 7) ?? d.fiscal_year ?? '',
        dps: d.dividend_per_share ?? 0,
    }))

    return (
        <div className="new-kb-tab institutional-tab">
            {/* 估值历史 */}
            <Card
                title="估值历史（近90天）"
                size="small"
                className="new-kb-card"
                extra={
                    <Radio.Group
                        value={valMetric}
                        onChange={e => setValMetric(e.target.value)}
                        size="small"
                        optionType="button"
                        options={[
                            { label: 'PE', value: 'pe' },
                            { label: 'PB', value: 'pb' },
                            { label: 'PS', value: 'ps' },
                        ]}
                    />
                }
            >
                {(valuation?.trend?.historical_items?.length ?? 0) === 0 ? (
                    <Empty description="暂无估值数据" image={Empty.PRESENTED_IMAGE_SIMPLE} style={{ padding: '16px 0' }} />
                ) : (
                    <ResponsiveContainer width="100%" height={180}>
                        <LineChart data={valChartData} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
                            <XAxis dataKey="date" tick={{ fontSize: 11 }} interval="preserveStartEnd" />
                            <YAxis tick={{ fontSize: 11 }} width={40} />
                            <Tooltip contentStyle={{ fontSize: 12 }} />
                            <Legend wrapperStyle={{ fontSize: 12 }} />
                            <Line
                                type="monotone"
                                dataKey={metricKey}
                                name={metricLabel[metricKey]}
                                stroke={metricColor[metricKey]}
                                dot={false}
                                strokeWidth={1.5}
                            />
                            {avgVal != null && (
                                <ReferenceLine
                                    y={avgVal}
                                    stroke="#faad14"
                                    strokeDasharray="4 2"
                                    label={{ value: `均值 ${avgVal.toFixed(1)}`, position: 'insideTopRight', fontSize: 10, fill: '#faad14' }}
                                />
                            )}
                        </LineChart>
                    </ResponsiveContainer>
                )}
            </Card>

            {/* 分析师评级 */}
            <Card title="分析师综合评级" size="small" className="new-kb-card">
                {analyst == null ? (
                    <Empty description="暂无分析师数据" image={Empty.PRESENTED_IMAGE_SIMPLE} style={{ padding: '16px 0' }} />
                ) : (
                    <div className="analyst-panel">
                        <div className="analyst-summary">
                            <Tag color={ratingColor} style={{ fontSize: 14, padding: '4px 12px' }}>
                                {analyst.rating ?? '--'}
                            </Tag>
                            <div className="analyst-meta">
                                <span>{analyst.total_analysts ?? '--'} 位分析师</span>
                                <span>目标价：{fmt(analyst.target_price_avg)} ({fmt(analyst.target_price_low)}~{fmt(analyst.target_price_high)})</span>
                                <span className="analyst-date">{analyst.snapshot_date ?? ''}</span>
                            </div>
                        </div>
                        <div className="analyst-distribution">
                            {[
                                { label: '强烈买入', val: analyst.strong_buy_pct, color: '#52c41a' },
                                { label: '买入', val: analyst.buy_pct, color: '#73d13d' },
                                { label: '持有', val: analyst.hold_pct, color: '#faad14' },
                                { label: '跑输', val: analyst.underperform_pct, color: '#ff7a45' },
                                { label: '卖出', val: analyst.sell_pct, color: '#ff4d4f' },
                            ].map(item => (
                                <div key={item.label} className="analyst-dist-row">
                                    <span className="analyst-dist-label">{item.label}</span>
                                    <Progress
                                        percent={item.val ?? 0}
                                        strokeColor={item.color}
                                        size="small"
                                        format={p => `${p?.toFixed(0)}%`}
                                        style={{ flex: 1, margin: '0 8px' }}
                                    />
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </Card>

            {/* 分红历史 */}
            <Card title="分红历史" size="small" className="new-kb-card">
                {dividends.length === 0 ? (
                    <Empty description="暂无分红数据" image={Empty.PRESENTED_IMAGE_SIMPLE} style={{ padding: '16px 0' }} />
                ) : (
                    <>
                        <ResponsiveContainer width="100%" height={140}>
                            <BarChart data={divChartData} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
                                <XAxis dataKey="period" tick={{ fontSize: 11 }} interval="preserveStartEnd" />
                                <YAxis tick={{ fontSize: 11 }} width={40} />
                                <Tooltip contentStyle={{ fontSize: 12 }} formatter={v => [v, '每股股息']} />
                                <Bar dataKey="dps" name="每股股息" fill="#faad14" radius={[2, 2, 0, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                        <div className="fin-table-wrap" style={{ marginTop: 8 }}>
                            <table className="fin-table">
                                <thead>
                                    <tr>
                                        <th>除权日</th>
                                        <th>财年</th>
                                        <th>每股分红</th>
                                        <th>货币</th>
                                        <th>派息率</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {dividends.map((d, i) => (
                                        <tr key={i}>
                                            <td>{d.ex_dividend_date ?? '--'}</td>
                                            <td>{d.fiscal_year ?? '--'}</td>
                                            <td>{fmt(d.dividend_per_share)}</td>
                                            <td>{d.currency ?? '--'}</td>
                                            <td>{fmt(d.payout_ratio, 1, '%')}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </>
                )}
            </Card>
        </div>
    )
}
